import StaffScanWaiting, { ScanLabel } from '../components/StaffScanWaiting';
/**
 * 주렁맨 — QR 스캔 → 주화 즉석 지급 (RF1·RF2·RF3)
 * ============================================================
 * 설계: `docs/2026-07-07-zoolungman-coin-grant-app-dev-steps.md` §S4
 *      / 기획 `docs/2026-07-07-zoolungman-coin-grant-app.md`
 *
 * 흐름은 **한 화면 연속 스캔** 3탭입니다 — `스캔 → 수량 → 확인`.
 *   회원 QR(`JRG_`) 과 QR밴드(`ZLB_`) 를 모두 받습니다(R4). 밴드 해석은 서버
 *   `GET /member/by-qr` 이 `allow_band=True` 로 처리합니다(`api-server/src/routers/member.py:93`).
 *
 * ⚠️ 판단 로직은 전부 `utils/zoolungmanUtils.js` 에 있습니다(단위테스트 대상).
 *    이 파일은 **조립·렌더만** 합니다. 새 문구/규칙을 여기 직접 쓰지 마십시오.
 */

import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Modal, ConfigProvider, theme as antdTheme } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, CheckOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { getMemberByQR, giveCoin } from '../services/api';
import { extractQRCode } from '../utils/qrUtils';
import coinImage from '../assets/service-menu/bank.png';
import waitingImage from '../assets/quest/zoolungman-scan.png';
import {
  DEFAULT_SCAN_ERROR_MESSAGE,
  invalidScanMessage,
  scanErrorMessage,
} from '../constants/scanErrorMessages';
import {
  GIVE_MAX_AMOUNT,
  QUICK_AMOUNTS,
  SAME_CODE_COOLDOWN_MS,
  giveErrorMessage,
  nextInvalidScanState,
  normalizeBalance,
  previewBalanceAfter,
  shouldSkipRescan,
  toMemberCard,
  validateGiveAmount,
} from '../utils/zoolungmanUtils';
import './ZoolungmanPage.css';
import './AllInOnePage.css';
import './BankWorkspace.css';
import './ZoolungmanWaiting.css';

export default function ZoolungmanPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const storeName = user?.store_name || localStorage.getItem('store_name') || '';


  const [member, setMember] = useState(null);           // 회원 카드 (toMemberCard 결과)
  const [scanMessage, setScanMessage] = useState(null); // 스캔/조회 실패 안내
  const [looking, setLooking] = useState(false);        // 회원 조회 중(스피너용)
  const [amount, setAmount] = useState(null);           // 확정된 지급 수량(숫자)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customValue, setCustomValue] = useState('');
  const [customError, setCustomError] = useState(null);
  const customValueRef = useRef('');
  const [submitting, setSubmitting] = useState(false);  // 지급 in-flight 잠금
  const [giveError, setGiveError] = useState(null);
  const [resultPopup, setResultPopup] = useState(null);

  // 🔴 아래 4개는 **반드시 ref** 입니다.
  //    `QRScanner` 는 `reader.decodeFromStream(..., callback)` 등록 시점의 클로저를 계속 씁니다
  //    (`components/QRScanner.js:88`). 즉 스캔 콜백은 **등록 당시의 handleScan** 이므로 state 를
  //    읽으면 영원히 그때 값을 봅니다. 가드 값은 전부 ref 로 읽습니다.
  const memberRef = useRef(null);         // 회원 선택 여부(중복 조회 차단)
  const lookingRef = useRef(false);       // 조회 in-flight
  const lastScanRef = useRef(null);       // { code, at } — 같은 QR 쿨다운
  const lastInvalidRawRef = useRef(null); // 직전 파싱실패 원문(D9 중복 억제)
  // 🔴 지급 in-flight 잠금의 **1차 방어**. `submitting` state 만으로 막으면 "리렌더가 끝났을 것"
  //    이라는 가정에 의존합니다. 값(주화) 지급이고 서버에 idempotency 가 없으므로,
  //    리렌더 타이밍과 무관하게 즉시 참이 되는 ref 를 함께 둡니다(태블릿 더블탭·고스트클릭 방어).
  const submittingRef = useRef(false);

  /** state 와 ref 를 함께 갱신합니다(스캔 콜백이 ref 를 보기 때문). */
  const applyMember = useCallback((next) => {
    memberRef.current = next;
    setMember(next);
  }, []);

  /** 다음 스캔을 받을 수 있도록 화면을 초기화합니다(연속 지급). */
  const resetForNextScan = useCallback(() => {
    applyMember(null);
    setResultPopup(null);
    setAmount(null);
    setConfirmOpen(false);
    setCustomOpen(false);
    setCustomError(null);
    setGiveError(null);
    setScanMessage(null);
    // 같은 회원에게 연달아 지급하는 경우가 있으므로 쿨다운 기록을 비웁니다.
    lastScanRef.current = null;
    lastInvalidRawRef.current = null;
  }, [applyMember]);

  /** QR 파싱 성공 → 회원 조회. 가드는 전부 ref(위 주석 참고). */
  const handleScan = useCallback(
    async (code) => {
      if (memberRef.current) return;   // 이미 회원 선택됨(스캐너 정지 직전 잔여 프레임)
      if (lookingRef.current) return;  // 조회 in-flight — 프레임마다 중복 호출 방지
      const now = Date.now();
      if (shouldSkipRescan(lastScanRef.current, code, now, SAME_CODE_COOLDOWN_MS)) return;

      lastScanRef.current = { code, at: now };
      lookingRef.current = true;
      setLooking(true);
      setScanMessage(null);

      try {
        const res = await getMemberByQR(code);
        const card = toMemberCard(res);
        if (!card) {
          // 200 인데 회원 식별 불가 — 무음 실패로 두지 않습니다.
          setScanMessage(DEFAULT_SCAN_ERROR_MESSAGE);
          return;
        }
        lastInvalidRawRef.current = null;
        applyMember(card);
      } catch (err) {
        // ⚠️ `services/api.js` 인터셉터가 에러를 평면 객체로 정규화하므로 `err.error_code` 입니다
        //    (`err.response.data.error_code` 아님 — services/api.js:110-129).
        setScanMessage(scanErrorMessage(err?.error_code));
      } finally {
        lookingRef.current = false;
        setLooking(false);
      }
    },
    [applyMember]
  );

  /** QR 파싱 실패(`extractQRCode` → null) — D9. 직전 원문과 같으면 무시합니다. */
  const handleInvalidScan = useCallback((rawValue) => {
    const next = nextInvalidScanState(lastInvalidRawRef.current, rawValue, invalidScanMessage);
    if (!next.changed) return;
    lastInvalidRawRef.current = next.lastRaw;
    setScanMessage(next.message);
  }, []);

  /** USB/네이티브 리더는 원문을 넘기므로 카메라와 동일한 회원·밴드 QR 파서를 거친다. */
  const handleUsbScan = useCallback((rawValue) => {
    const code = extractQRCode(rawValue);
    if (!code) {
      handleInvalidScan(rawValue);
      return;
    }
    return handleScan(code);
  }, [handleInvalidScan, handleScan]);

  // 현장 태블릿의 USB QR 리더와 네이티브 키오스크 스캔 이벤트를 함께 수신한다.
  // 회원이 잡힌 뒤에는 지급 완료/다시 스캔 전까지 새 입력을 받지 않는다.
  useUsbQrReader({
    enabled: !member && !looking,
    onScan: handleUsbScan,
    minLength: 8,
  });

  const pickQuickAmount = (value) => {
    setAmount(value);
    setGiveError(null);
    setConfirmOpen(true);
  };

  const openCustomModal = () => {
    customValueRef.current = '';
    setCustomValue('');
    setCustomError(null);
    setCustomOpen(true);
  };

  const submitCustomAmount = () => {
    const result = validateGiveAmount(customValue);
    if (!result.ok) {
      setCustomError(result.message);
      return;
    }
    setAmount(result.amount);
    setCustomOpen(false);
    setGiveError(null);
    setConfirmOpen(true);
  };

  const handleGive = async () => {
    if (submittingRef.current || submitting) return;
    if (!member || amount === null) return;
    submittingRef.current = true;
    setSubmitting(true);
    setGiveError(null);
    try {
      const res = await giveCoin({ memberId: member.memberId, amount });
      const granted = normalizeBalance(res?.amount ?? amount);
      const newBalance = normalizeBalance(res?.balance_after);
      const name = member.displayName;
      applyMember({ ...member, balance: newBalance });
      setConfirmOpen(false);
      setAmount(null);
      setResultPopup({ ownerName: name, amount: granted, balance: newBalance });
    } catch (err) {
      setGiveError(giveErrorMessage(err));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const enterCustomQuantity = (key) => {
    const value = customValueRef.current;
    const next = key === 'clear' ? '' : key === 'backspace' ? value.slice(0, -1) : (value === '0' ? '' : value) + key;
    const exceeded = Number(next) > GIVE_MAX_AMOUNT;
    const adjusted = exceeded ? String(GIVE_MAX_AMOUNT) : next;
    customValueRef.current = adjusted;
    setCustomValue(adjusted);
    setCustomError(exceeded ? `1회 최대 ${GIVE_MAX_AMOUNT}주화까지 지급할 수 있어 ${GIVE_MAX_AMOUNT}으로 조정했습니다.` : null);
  };
  const customValidation = validateGiveAmount(customValue);
  const balanceAfter = member && amount !== null ? previewBalanceAfter(member.balance, amount) : 0;

  return (
    <div className={`bank-workspace-page zm-page${!member ? ' zm-is-waiting' : ''}`}>
      <header className="all-header">
        <div className="all-header-left">
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>메뉴</Button>
          <span className="all-header-title">주렁맨</span>
        </div>
        <div className="all-header-right">
          {member && <Button icon={<ReloadOutlined />} onClick={resetForNextScan} disabled={submitting || confirmOpen || customOpen || !!resultPopup} data-testid="zoolungman-rescan">다시 스캔</Button>}
          {storeName && <span className="bank-store-badge"><i aria-hidden="true" />{storeName}</span>}
          <span className="all-user-name"><small>ID</small><strong>{user?.name || user?.username || '운영자'}</strong></span>
        </div>
      </header>
      <div className="zm-content">
        {/* ───── 우: 회원 카드 + 수량 ───── */}
        <section className="zm-right">
          {!member ? (
            <StaffScanWaiting image={waitingImage} alt="주렁맨이 탐험가의 주렁밴드를 스캔하는 모습" busy={looking} busyText="회원 조회 중…" onScan={handleUsbScan} error={scanMessage} guide={<><ScanLabel>회원QR</ScanLabel><span>또는</span><span className="ssw-phrase"><ScanLabel>주렁밴드QR</ScanLabel><span>을 스캔해주세요.</span></span></>} />
          ) : (
            <>
              <div className="zm-member-card">
                <p className="zm-member-label">회원 정보</p>
                <div className="zm-member-identity">
                  <span className="zm-member-name-label">선택된 회원</span>
                  <p className="zm-member-name">{member.displayName}</p>
                </div>
                <div className="zm-member-balance">
                  <img className="zm-member-coin-image" src={coinImage} alt="주렁주화" />
                  <div className="zm-member-balance-copy">
                    <span className="zm-member-balance-label">현재 보유 주화</span>
                    <div className="zm-member-balance-amount">
                      {/* 🔴 D8 — 잔액행이 없는 회원은 서버가 null 을 주므로 toMemberCard 가 0 으로 정규화 */}
                      <span className="zm-member-balance-value">{member.balance}</span>
                      <span className="zm-member-balance-unit">개</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="zm-amount-block">
                <p className="zm-amount-title">지급수량</p>
                <div className="zm-amount-buttons">
                  {QUICK_AMOUNTS.map((value) => (
                    <button
                      key={value}
                      className="zm-btn-amount"
                      disabled={submitting || !!resultPopup}
                      onClick={() => pickQuickAmount(value)}
                    >
                      <span className="zm-amount-number">{value}</span>
                      <span className="zm-amount-unit">주화</span>
                    </button>
                  ))}
                <button
                  className="zm-btn-amount zm-btn-amount-custom"
                  disabled={submitting || !!resultPopup}
                  onClick={openCustomModal}
                >
                  직접입력
                </button>
                </div>
                <p className="zm-amount-hint">
                  1회 최대 {GIVE_MAX_AMOUNT}주화까지 지급할 수 있습니다
                </p>
              </div>
            </>
          )}
        </section>
      </div>

      {/* ───── 직접입력 모달 (RF2) ───── */}
      <ConfigProvider theme={{ algorithm: antdTheme.defaultAlgorithm, token: { colorPrimary:'#007800', fontFamily:'"Noto Sans KR", sans-serif' } }}>
      <Modal open={customOpen} onCancel={() => setCustomOpen(false)} footer={null} centered width={1020}
        destroyOnHidden title={null} aria-label="지급 수량 직접입력" rootClassName="zb-operation-root"
        className="zb-operation-modal zm-quantity-modal" transitionName="" maskTransitionName="">
        <div className="zb-desk zb-transaction">
          <section className="zb-entry" aria-label="지급 수량 입력">
            <div className="zb-entry-head"><h2>지급수량</h2></div>
            <div className="zb-quantity">
              <input className="zm-quantity-display" aria-label="지급할 주화 수량" value={customValue || '0'} readOnly inputMode="none" autoFocus onKeyDown={event => {
                if (/^[0-9]$/.test(event.key)) { event.preventDefault(); enterCustomQuantity(event.key); }
                else if (event.key === 'Backspace' || event.key === 'Delete') { event.preventDefault(); enterCustomQuantity(event.key === 'Backspace' ? 'backspace' : 'clear'); }
                else if (event.key === 'Enter') { event.preventDefault(); submitCustomAmount(); }
              }} /><span>개</span>
            </div>
            <div className="zb-keypad">
              {['1','2','3','4','5','6','7','8','9','clear','0','backspace'].map(key => <button type="button" key={key} onClick={() => enterCustomQuantity(key)} data-testid={'zoolungman-key-' + key} aria-label={key === 'clear' ? '수량 초기화' : key === 'backspace' ? '한 자리 지우기' : key}>
                {key === 'clear' ? '초기화' : key === 'backspace' ? '⌫' : key}
              </button>)}
            </div>
          </section>
          <aside className="zb-review" aria-label="지급 수량 확인">
            <dl>
              <div className="zb-selected-quantity"><dt>선택한 수량</dt><dd>{customValue || '0'}<small>개</small></dd></div>
              <div className="zb-after"><dt>지급 후 잔액</dt><dd>{customValidation.ok && member ? previewBalanceAfter(member.balance, customValidation.amount).toLocaleString() : '–'}<small>개</small></dd></div>
            </dl>
            <p className="zb-review-note">수량을 확인한 뒤 다음 화면에서 지급을 확정합니다.</p>
            {customError && <div className="zb-error" role="alert">{customError}</div>}
            <button type="button" className="zb-submit" onClick={submitCustomAmount} data-testid="zoolungman-quantity-confirm">수량 확인</button>
          </aside>
        </div>
      </Modal>


      <Modal open={!!resultPopup} footer={null} closable onCancel={() => setResultPopup(null)}
        centered width={560} className="zb-result-modal" rootClassName="zb-operation-root"
        aria-label="주화 지급 완료" destroyOnHidden transitionName="" maskTransitionName="">
        {resultPopup && <section className="zb-result" data-testid="zoolungman-result">
          <span className="zb-result-mark" aria-hidden="true"><CheckOutlined /></span>
          <h2>지급 완료!</h2>
          <dl className="zb-result-details">
            <div><dt>대상자</dt><dd>{resultPopup.ownerName}</dd></div>
            <div><dt>지급수량</dt><dd>{resultPopup.amount.toLocaleString()}<small>개</small></dd></div>
          </dl>
          <div className="zb-result-balance"><span>현재 잔액</span><strong>{resultPopup.balance.toLocaleString()}<small>개</small></strong></div>
          <Button type="primary" className="zb-result-confirm" block onClick={() => setResultPopup(null)}>확인</Button>
        </section>}
      </Modal>
      </ConfigProvider>

      {/* ───── 확인 모달 (RF3) ───── */}
      {confirmOpen && member && amount !== null && (
        <div className="zm-modal-backdrop">
          <div className="zm-modal">
            <h2 className="zm-modal-title">주화를 지급할까요?</h2>
            <dl className="zm-confirm-list">
              <div className="zm-confirm-row">
                <dt>회원</dt>
                <dd>{member.displayName}</dd>
              </div>
              <div className="zm-confirm-row">
                <dt>지급 수량</dt>
                <dd className="zm-confirm-amount">{amount}주화</dd>
              </div>
              <div className="zm-confirm-row">
                <dt>지급 후 잔액</dt>
                <dd>
                  {member.balance} → <strong>{balanceAfter}</strong>주화
                </dd>
              </div>
            </dl>
            {giveError && <p className="zm-modal-error">{giveError}</p>}
            <div className="zm-modal-actions">
              <button
                className="zm-btn-cancel"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
              >
                취소
              </button>
              {/* 🔴 in-flight 잠금 — 서버에 idempotency 가 없어 더블탭이 곧 중복지급입니다 */}
              <button className="zm-btn-primary" onClick={handleGive} disabled={submitting}>
                {submitting ? '지급 중...' : '지급'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
